import { useState, useEffect, useRef, useMemo } from "react";
import { Nav, NavItem, NavLink, Button, Row, Col, Badge } from "reactstrap";
import Select, { createFilter } from "react-select";
import { FormattedMessage, useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { AplPeriodGroup } from "../../../constants/AplPeriodGroup";

const filterByLabel = createFilter({ stringify: (option) => option.label });

const textMatchesSearch = (text, searchText) =>
  Boolean(text) && filterByLabel({ label: text, data: {} }, searchText);

const periodMatchesSearch = (period, searchText) =>
  textMatchesSearch(period.label, searchText) ||
  period.students.some(
    (student) =>
      textMatchesSearch(student.name, searchText) ||
      textMatchesSearch(student.className, searchText),
  );

function APLTabs({
  toggleCreatePeriod,
  toggleExportModal,
  toggleEditSupervisors,
  periodGroups,
  period,
  activeTab,
  handleSelectPeriod,
  selectedPeriod,
  handleSetAplTab,
  isMobile = false,
  unviewedCount = 0,
  unviewedCountsByPeriod = {},
}) {
  const intl = useIntl();
  const fadeTimerRef = useRef(null);
  const [badgeVisible, setBadgeVisible] = useState(unviewedCount > 0);
  const [badgeFadingOut, setBadgeFadingOut] = useState(false);
  const displayCountRef = useRef(unviewedCount);
  const periodSelectRef = useRef(null);
  const [openGroups, setOpenGroups] = useState([AplPeriodGroup.ONGOING]);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }

    if (unviewedCount > 0) {
      displayCountRef.current = unviewedCount;
      setBadgeVisible(true);
      setBadgeFadingOut(false);
    } else if (badgeVisible) {
      setBadgeFadingOut(true);
      fadeTimerRef.current = setTimeout(() => {
        setBadgeVisible(false);
        setBadgeFadingOut(false);
      }, 400);
    }
    return () => {
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
      }
    };
  }, [unviewedCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedPeriodGroup = selectedPeriod?.group;
  const isSearching = searchText.trim() !== "";

  useEffect(() => {
    if (selectedPeriodGroup) {
      setOpenGroups((current) =>
        current.includes(selectedPeriodGroup)
          ? current
          : [...current, selectedPeriodGroup],
      );
    }
  }, [selectedPeriodGroup]);

  const linkClass = (id) =>
    classNames("apl-subtab-link", {
      "apl-subtab-link--active": activeTab === id,
      "apl-subtab-link--inactive": activeTab !== id,
    });

  const periodOptions = useMemo(
    () =>
      periodGroups.flatMap((periodGroup) => {
        const matchingPeriods = periodGroup.periods.filter((option) =>
          periodMatchesSearch(option, searchText),
        );
        if (matchingPeriods.length === 0) return [];

        const headingLabel = `${intl.formatMessage({ id: periodGroup.group })} (${matchingPeriods.length})`;
        if (isSearching) {
          return [{ label: headingLabel, options: matchingPeriods }];
        }

        return [
          {
            value: `heading-${periodGroup.group}`,
            label: headingLabel,
            isGroupHeading: true,
            group: periodGroup.group,
            unviewedTotal: matchingPeriods.reduce(
              (total, periodOption) =>
                total + (unviewedCountsByPeriod[periodOption.value] || 0),
              0,
            ),
          },
          ...(openGroups.includes(periodGroup.group) ? matchingPeriods : []),
        ];
      }),
    [
      periodGroups,
      openGroups,
      searchText,
      isSearching,
      unviewedCountsByPeriod,
      intl,
    ],
  );

  const toggleGroup = (group) =>
    setOpenGroups((current) =>
      current.includes(group)
        ? current.filter((openGroup) => openGroup !== group)
        : [...current, group],
    );

  const resetOpenGroups = () =>
    setOpenGroups([
      ...new Set([AplPeriodGroup.ONGOING, selectedPeriodGroup].filter(Boolean)),
    ]);

  const formatGroupHeading = (option) => {
    const isOpen = openGroups.includes(option.group);
    return (
      <div className="d-flex align-items-center justify-content-between">
        <span className="fw-bold">
          <FontAwesomeIcon
            className="me-2"
            icon={isOpen ? "chevron-down" : "chevron-right"}
          />
          {option.label}
        </span>
        {!isOpen && option.unviewedTotal > 0 ? (
          <Badge color="danger" pill className="ms-2">
            {option.unviewedTotal}
          </Badge>
        ) : (
          ""
        )}
      </div>
    );
  };

  const formatPeriodOptionLabel = (option, { context }) => {
    if (option.isGroupHeading) {
      return formatGroupHeading(option);
    }
    const count = unviewedCountsByPeriod[option.value] || 0;
    const showsMatchDetails = context === "menu" && isSearching;
    const matchingStudents = showsMatchDetails
      ? option.students.filter((student) =>
          textMatchesSearch(student.name, searchText),
        )
      : [];
    const matchingClassNames = showsMatchDetails
      ? [
          ...new Set(option.students.map((student) => student.className)),
        ].filter((className) => textMatchesSearch(className, searchText))
      : [];
    const detailCssClass = classNames("small", {
      "text-body-secondary": option.value !== selectedPeriod?.value,
    });
    return (
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <span>{option.label}</span>
          {matchingStudents.slice(0, 3).map((student) => (
            <div className={detailCssClass} key={student.id}>
              <FormattedMessage id="Student" />: {student.name}
              {student.className ? (
                <>
                  {" · "}
                  <FormattedMessage id="Class" />: {student.className}
                </>
              ) : (
                ""
              )}
            </div>
          ))}
          {matchingStudents.length > 3 ? (
            <div className={detailCssClass}>
              <FormattedMessage
                id="MoreMatchingAplStudents"
                values={{ count: matchingStudents.length - 3 }}
              />
            </div>
          ) : (
            ""
          )}
          {matchingStudents.length === 0 && matchingClassNames.length > 0 ? (
            <div className={detailCssClass}>
              <FormattedMessage id="Class" />: {matchingClassNames.join(", ")}
            </div>
          ) : (
            ""
          )}
        </div>
        {count > 0 ? (
          <Badge color="danger" pill className="ms-2">
            {count}
          </Badge>
        ) : (
          ""
        )}
      </div>
    );
  };

  const tabs = [
    { id: 4, messageId: "AdministerPeriod" },
    { id: 1, messageId: "Presence" },
    { id: 2, messageId: "Workcard" },
    { id: 3, messageId: "WorkcardFeed" },
  ];

  const periodSelector = (
    <Select
      ref={periodSelectRef}
      menuPortalTarget={document.body}
      className={isMobile ? "mx-3" : undefined}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) =>
          state.data.isGroupHeading ? { ...base, cursor: "pointer" } : base,
        groupHeading: (base) => ({
          ...base,
          color: "inherit",
          fontSize: "inherit",
          fontWeight: 700,
          textTransform: "none",
        }),
      }}
      menuPosition="fixed"
      options={periodOptions}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      tabSelectsValue={false}
      filterOption={null}
      onMenuOpen={resetOpenGroups}
      inputValue={searchText}
      onInputChange={(text) => setSearchText(text)}
      value={selectedPeriod || null}
      onChange={(option) => {
        if (option.isGroupHeading) {
          toggleGroup(option.group);
          return;
        }
        periodSelectRef.current.blur();
        handleSelectPeriod(option);
      }}
      aria-label={intl.formatMessage({ id: "SearchAplPeriods" })}
      placeholder={intl.formatMessage({ id: "SearchAplPeriods" })}
      noOptionsMessage={() =>
        intl.formatMessage({ id: "NoMatchingAplPeriods" })
      }
      formatOptionLabel={formatPeriodOptionLabel}
    />
  );

  return (
    <div>
      <div
        className={isMobile ? "teacher-tab--mobile" : "apl-subtabs-no-scroll"}
      >
        <Nav
          tabs
          className={classNames("apl-subtabs", {
            "teacher-tab--mobile": isMobile,
          })}
        >
          {period &&
            tabs.map((t) => (
              <NavItem key={t.id}>
                <NavLink
                  className={linkClass(t.id)}
                  active={activeTab === t.id}
                  onClick={() => handleSetAplTab(t.id)}
                >
                  <FormattedMessage id={t.messageId} />
                  {t.id === 3 && badgeVisible && (
                    <Badge
                      color="danger"
                      pill
                      className={classNames("ms-2 apl-badge-notify", {
                        "apl-badge-notify--out": badgeFadingOut,
                      })}
                    >
                      {displayCountRef.current}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            ))}
        </Nav>
      </div>

      <div className="mt-3">
        {isMobile ? (
          <div className="d-flex flex-column">
            <Button
              color="primary"
              className="m-3"
              onClick={toggleCreatePeriod}
            >
              <FontAwesomeIcon icon="plus" className="me-1" />
              <FormattedMessage id="NewAplPeriod" />
            </Button>
            <Button
              color="primary"
              outline
              className="ms-3 me-3 mb-3"
              onClick={toggleEditSupervisors}
            >
              <FontAwesomeIcon icon="user-edit" className="me-1" />
              <FormattedMessage id="EditSupervisors" />
            </Button>
            {period && (
              <Button
                color="primary"
                outline
                className="ms-3 me-3 mb-3"
                onClick={toggleExportModal}
              >
                <FontAwesomeIcon icon="file-excel" className="me-1" />
                <FormattedMessage id="ExportToExcel" />
              </Button>
            )}

            {periodSelector}
          </div>
        ) : (
          <Row className="align-items-center">
            <Col xs="10" md="6" lg="5" className="ms-2 pe-1">
              {periodSelector}
            </Col>
            <Col className="ps-1">
              <Button color="primary" onClick={toggleCreatePeriod}>
                <FontAwesomeIcon icon="plus" className="me-1" />
                <FormattedMessage id="NewAplPeriod" />
              </Button>
              <Button
                color="primary"
                outline
                className="ms-2"
                onClick={toggleEditSupervisors}
              >
                <FontAwesomeIcon icon="user-edit" className="me-1" />
                <FormattedMessage id="EditSupervisors" />
              </Button>
              {period && (
                <Button
                  color="primary"
                  outline
                  className="ms-2"
                  onClick={toggleExportModal}
                >
                  <FontAwesomeIcon icon="file-excel" className="me-1" />
                  <FormattedMessage id="ExportToExcel" />
                </Button>
              )}
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
}

export default APLTabs;
